import { useEffect, useState } from "react";
import { useMutation, useQuery } from "@apollo/client";
import { CREATE_EMPLOYMENT_MUTATION } from "../../person/mutations";
import EmploymentFormModal, { EmploymentFormData } from "./EmploymentFormModal";
import { GET_JOB_TITLES_QUERY, EmploymentRow } from "../../person/queries";
import { PERSON_DETAILS_HEADER_QUERY } from "@/features/person/queries";
import { useParams } from "@/router";
import { PollForBandChangesFunction } from "@/hooks/usePollBandChanges";
import { Band } from "@/types";
import EmailPreviewDialog from "@/components/EmailPreviewDialog";
import { useEmployerChangeAlertEmailPreview } from "../_hooks/useEmployerChangeAlertEmailPreview";
import type { EmployerChangeAlertEmailPreviewVariables } from "../_queries/emailPreviewQueries";

interface CreateEmploymentModalProps {
  personId?: string;
  onClose(): void;
  employments?: EmploymentRow[];
  pollForBandChanges: PollForBandChangesFunction;
  fetchCurrentBands: (pgaId: string) => Promise<Band[]>;
  employerChangeAlertPreviewEligible?: boolean;
}

export default function CreateEmploymentModal({
  personId,
  onClose,
  employments = [],
  pollForBandChanges,
  fetchCurrentBands,
  employerChangeAlertPreviewEligible = false
}: CreateEmploymentModalProps) {
  const { pgaId } = useParams("/people/:pgaId/career/employment");
  const [previewOpen, setPreviewOpen] = useState(false);
  const { data: jobTitlesData } = useQuery(GET_JOB_TITLES_QUERY, {
    variables: { personId: personId! },
    skip: !personId
  });
  const {
    loadPreview,
    resetPreview,
    preview,
    loading: previewLoading,
    error: previewError
  } = useEmployerChangeAlertEmailPreview();

  useEffect(() => () => resetPreview(), [resetPreview]);

  const availableJobTitles = jobTitlesData?.person.availableJobTitles || [];
  const tourCodes = jobTitlesData?.tourCodes || [];

  const [createEmploymentMutation] = useMutation(CREATE_EMPLOYMENT_MUTATION, {
    refetchQueries: [
      {
        query: PERSON_DETAILS_HEADER_QUERY,
        variables: { pgaId }
      }
    ],
    awaitRefetchQueries: true
  });

  const handlePreviewEmployerChangeAlert = (params: EmployerChangeAlertEmailPreviewVariables) => {
    setPreviewOpen(true);
    void loadPreview(params);
  };

  const onSubmit = async ({ workingLocation, note, ...formData }: EmploymentFormData) => {
    // Capture baseline BEFORE mutation to ensure we detect new bands
    let baselineBands: Band[] | undefined;

    try {
      baselineBands = await fetchCurrentBands(pgaId);
    } catch (e) {
      console.warn("[CreateEmployment] Failed to capture baseline, will capture during polling:", e);
    }

    const input = {
      personId,
      ...formData,
      workingLocation: workingLocation === "ON_SITE" ? "on_site" : "remote",
      note: note ? { content: note, priority: "NONE", title: "Add Employment" } : undefined
    };
    const { data } = await createEmploymentMutation({
      variables: {
        input
      }
    });

    const result = data?.createEmployment || { success: false, message: "Failed to create employment" };

    // If mutation succeeded, poll for band changes
    // Use pgaId from route params since the query requires pgaId, not person.id
    if (result.success) {
      // Try to get facility section from the newly created employment in the response
      const newEmployment = data?.createEmployment?.employment?.person?.employments?.find(
        (emp: EmploymentRow) => emp.facility?.id === formData.facilityId && emp.startedOn === formData.startedOn
      );
      const facilitySectionLegacyId = newEmployment?.facility?.section?.legacyId;

      const expectedChanges = {
        classificationName: formData.jobClassification,
        startedOn: formData.startedOn,
        sectionLegacyId: facilitySectionLegacyId
      };

      // Poll for band changes in the background, passing baseline if we captured it
      pollForBandChanges(pgaId, expectedChanges, baselineBands).catch((error) => {
        console.error("[CreateEmployment] Error polling for band changes:", error);
      });
    }

    return result;
  };

  return (
    <>
      <EmploymentFormModal
        open
        personId={personId}
        headline="Create Employment"
        availableJobTitles={availableJobTitles}
        tourCodes={tourCodes}
        onSubmit={onSubmit}
        onClose={onClose}
        employments={employments}
        showEmployerChangeAlertPreview={employerChangeAlertPreviewEligible}
        onPreviewEmployerChangeAlert={handlePreviewEmployerChangeAlert}
      />
      <EmailPreviewDialog
        open={previewOpen}
        onClosed={() => setPreviewOpen(false)}
        subject={previewLoading ? undefined : preview?.subject}
        html={previewLoading ? undefined : preview?.html}
        loading={previewLoading}
        errorMessage={previewLoading ? null : (previewError?.message ?? null)}
      />
    </>
  );
}
